import React, { createContext, useContext, useState } from 'react';

const translations = {
  fr: {
    // Navigation
    nav: {
      home: "Accueil",
      howItWorks: "Comment ça marche",
      solutions: "Solutions",
      pricing: "Tarification",
      partners: "Partenaires",
      popupPhoto: "Pop-up Photo",
      contact: "Contact",
      demo: "Demander une démo"
    },
    // Hero Section
    hero: {
      tagline: "La photographie du futur",
      title: "Réinventez l'expérience photo de vos clients",
      subtitle: "La solution 100% digitale, IA et mobile pour les hôtels & resorts",
      cta: "Découvrir la solution",
      ctaSecondary: "Voir comment ça marche"
    },
    // How it works
    howItWorks: {
      title: "Comment ça marche",
      subtitle: "Un parcours client fluide en 5 étapes",
      steps: [
        {
          number: "01",
          title: "Capture",
          description: "Le photographe immortalise les moments forts : activités, dîners, excursions"
        },
        {
          number: "02", 
          title: "Upload",
          description: "Chargement sécurisé sur l'espace iFotolife pro"
        },
        {
          number: "03",
          title: "IA & Reconnaissance",
          description: "Amazon Rekognition identifie les visages instantanément"
        },
        {
          number: "04",
          title: "Notification",
          description: "Le client reçoit une alerte ou scanne un QR code"
        },
        {
          number: "05",
          title: "Accès",
          description: "Visualisation, achat et partage immédiat"
        }
      ]
    },
    // Features
    features: {
      title: "Une technologie de pointe",
      subtitle: "Simple, Sécurisée, Instantanée",
      items: [
        {
          title: "100% Mobile & Cloud",
          description: "Aucune infrastructure lourde requise. Accédez à vos photos partout."
        },
        {
          title: "Intelligence Artificielle",
          description: "Reconnaissance faciale instantanée avec Amazon Rekognition."
        },
        {
          title: "Qualité Professionnelle",
          description: "Photos HD prises par des photographes professionnels."
        },
        {
          title: "Paiement Sécurisé",
          description: "Stripe, Apple Pay, Google Pay - transactions sécurisées."
        }
      ]
    },
    // Business Model
    businessModel: {
      title: "Un modèle économique gagnant-gagnant",
      subtitle: "Générez des revenus sans effort",
      commission: "de commission reversée",
      investment: "d'investissement",
      noSetup: "Aucun coût d'installation",
      noManagement: "Aucun frais de gestion",
      passive: "Revenu passif garanti",
      transparent: "Transparence totale"
    },
    // Solutions
    solutions: {
      title: "Solutions clé en main",
      subtitle: "Des options flexibles adaptées à vos besoins",
      app: {
        title: "Application Mobile",
        description: "Solution 100% digitale sans matériel, prête à l'emploi"
      },
      kiosk: {
        title: "Borne Kiosque",
        description: "Installation d'écrans tactiles dans le Lobby ou le Kids Club"
      },
      staffing: {
        title: "Staffing",
        description: "Photographes partenaires iFotolife ou formation de votre équipe"
      }
    },
    // Partners
    partners: {
      title: "Ils nous font confiance",
      subtitle: "Rejoignez les leaders de l'hôtellerie"
    },
    // Advantages
    advantages: {
      title: "Pourquoi choisir iFotolife ?",
      items: [
        {
          title: "Service Différenciant",
          description: "Une image innovante et connectée qui modernise votre établissement"
        },
        {
          title: "Satisfaction Client",
          description: "Accès facile et multilingue aux souvenirs de vacances"
        },
        {
          title: "Zéro Friction",
          description: "Interface intuitive sans assistance de la réception"
        },
        {
          title: "Automatisé",
          description: "De la prise de vue à la vente, la technologie gère tout"
        }
      ]
    },
    // Contact
    contact: {
      title: "Contactez-nous",
      subtitle: "Notre équipe est à votre écoute",
      form: {
        name: "Nom complet",
        email: "Email professionnel",
        company: "Nom de l'établissement",
        phone: "Téléphone",
        message: "Votre message",
        submit: "Envoyer le message",
        success: "Message envoyé avec succès !",
        error: "Une erreur est survenue. Veuillez réessayer."
      }
    },
    // Popup Photo Page
    popup: {
      title: "Pop-up Photo",
      subtitle: "Installez un studio photo dans votre établissement",
      hero: {
        title: "Créez des souvenirs inoubliables",
        description: "Offrez à vos clients une expérience photo unique avec notre solution de pop-up photo clé en main"
      },
      configurator: {
        title: "Configurez votre installation",
        establishment: {
          title: "Votre établissement",
          name: "Nom de l'établissement",
          type: "Type d'établissement",
          types: {
            hotel: "Hôtel",
            resort: "Resort",
            camping: "Camping de luxe",
            event: "Événementiel"
          },
          location: "Localisation",
          rooms: "Nombre de chambres/emplacements"
        },
        solution: {
          title: "Solution souhaitée",
          appOnly: "Application mobile uniquement",
          kiosk: "Borne kiosque",
          both: "Application + Borne"
        },
        staffing: {
          title: "Option staffing",
          ifotolife: "Photographes iFotolife",
          ownTeam: "Formation de votre équipe",
          mixed: "Solution mixte"
        },
        branding: {
          title: "Personnalisation",
          custom: "Branding personnalisé",
          backdrop: "Style de backdrop"
        },
        contact: {
          title: "Vos coordonnées",
          name: "Nom du contact",
          email: "Email",
          phone: "Téléphone"
        },
        notes: "Notes additionnelles",
        submit: "Demander l'installation",
        success: "Demande envoyée avec succès !",
        error: "Une erreur est survenue. Veuillez réessayer."
      },
      benefits: {
        title: "Avantages du Pop-up Photo",
        items: [
          "Expérience client unique",
          "Revenus additionnels",
          "Visibilité sur les réseaux sociaux",
          "Installation rapide et flexible"
        ]
      }
    },
    // Footer
    footer: {
      description: "La solution photo innovante pour l'hôtellerie",
      contact: "Contact",
      legal: "Mentions légales",
      privacy: "Confidentialité",
      terms: "CGU",
      rights: "Tous droits réservés"
    },
    // CTA
    cta: {
      title: "Prêt à révolutionner l'expérience photo de vos clients ?",
      subtitle: "Rejoignez les leaders de l'hôtellerie qui nous font confiance",
      button: "Demander une démo gratuite"
    }
  },
  en: {
    // Navigation
    nav: {
      home: "Home",
      howItWorks: "How it works",
      solutions: "Solutions",
      pricing: "Pricing",
      partners: "Partners",
      popupPhoto: "Photo Pop-up",
      contact: "Contact",
      demo: "Request a demo"
    },
    // Hero Section
    hero: {
      tagline: "The future of photography",
      title: "Reinvent your guests' photo experience",
      subtitle: "The 100% digital, AI-powered and mobile solution for hotels & resorts",
      cta: "Discover our solution",
      ctaSecondary: "See how it works"
    },
    // How it works
    howItWorks: {
      title: "How it works",
      subtitle: "A seamless customer journey in 5 steps",
      steps: [
        {
          number: "01",
          title: "Capture",
          description: "The photographer captures key moments: activities, dinners, excursions"
        },
        {
          number: "02",
          title: "Upload",
          description: "Secure upload to the iFotolife pro space"
        },
        {
          number: "03",
          title: "AI & Recognition",
          description: "Amazon Rekognition instantly identifies faces"
        },
        {
          number: "04",
          title: "Notification",
          description: "The customer receives an alert or scans a QR code"
        },
        {
          number: "05",
          title: "Access",
          description: "View, purchase and share instantly"
        }
      ]
    },
    // Features
    features: {
      title: "Cutting-edge technology",
      subtitle: "Simple, Secure, Instant",
      items: [
        {
          title: "100% Mobile & Cloud",
          description: "No heavy infrastructure required. Access your photos anywhere."
        },
        {
          title: "Artificial Intelligence",
          description: "Instant facial recognition with Amazon Rekognition."
        },
        {
          title: "Professional Quality",
          description: "HD photos taken by professional photographers."
        },
        {
          title: "Secure Payment",
          description: "Stripe, Apple Pay, Google Pay - secure transactions."
        }
      ]
    },
    // Business Model
    businessModel: {
      title: "A win-win business model",
      subtitle: "Generate revenue effortlessly",
      commission: "commission paid back",
      investment: "investment",
      noSetup: "No installation costs",
      noManagement: "No management fees",
      passive: "Guaranteed passive income",
      transparent: "Total transparency"
    },
    // Solutions
    solutions: {
      title: "Turnkey solutions",
      subtitle: "Flexible options tailored to your needs",
      app: {
        title: "Mobile Application",
        description: "100% digital solution without hardware, ready to use"
      },
      kiosk: {
        title: "Kiosk Booth",
        description: "Installation of touchscreens in the Lobby or Kids Club"
      },
      staffing: {
        title: "Staffing",
        description: "iFotolife partner photographers or training for your team"
      }
    },
    // Partners
    partners: {
      title: "They trust us",
      subtitle: "Join the hospitality leaders"
    },
    // Advantages
    advantages: {
      title: "Why choose iFotolife?",
      items: [
        {
          title: "Differentiating Service",
          description: "An innovative and connected image that modernizes your establishment"
        },
        {
          title: "Customer Satisfaction",
          description: "Easy and multilingual access to vacation memories"
        },
        {
          title: "Zero Friction",
          description: "Intuitive interface without front desk assistance"
        },
        {
          title: "Automated",
          description: "From shooting to selling, technology handles everything"
        }
      ]
    },
    // Contact
    contact: {
      title: "Contact us",
      subtitle: "Our team is here to help",
      form: {
        name: "Full name",
        email: "Professional email",
        company: "Establishment name",
        phone: "Phone",
        message: "Your message",
        submit: "Send message",
        success: "Message sent successfully!",
        error: "An error occurred. Please try again."
      }
    },
    // Popup Photo Page
    popup: {
      title: "Photo Pop-up",
      subtitle: "Install a photo studio in your establishment",
      hero: {
        title: "Create unforgettable memories",
        description: "Offer your guests a unique photo experience with our turnkey photo pop-up solution"
      },
      configurator: {
        title: "Configure your installation",
        establishment: {
          title: "Your establishment",
          name: "Establishment name",
          type: "Establishment type",
          types: {
            hotel: "Hotel",
            resort: "Resort",
            camping: "Luxury camping",
            event: "Events"
          },
          location: "Location",
          rooms: "Number of rooms/units"
        },
        solution: {
          title: "Desired solution",
          appOnly: "Mobile app only",
          kiosk: "Kiosk booth",
          both: "App + Booth"
        },
        staffing: {
          title: "Staffing option",
          ifotolife: "iFotolife photographers",
          ownTeam: "Train your team",
          mixed: "Mixed solution"
        },
        branding: {
          title: "Customization",
          custom: "Custom branding",
          backdrop: "Backdrop style"
        },
        contact: {
          title: "Your contact details",
          name: "Contact name",
          email: "Email",
          phone: "Phone"
        },
        notes: "Additional notes",
        submit: "Request installation",
        success: "Request sent successfully!",
        error: "An error occurred. Please try again."
      },
      benefits: {
        title: "Photo Pop-up Benefits",
        items: [
          "Unique customer experience",
          "Additional revenue",
          "Social media visibility",
          "Quick and flexible installation"
        ]
      }
    },
    // Footer
    footer: {
      description: "The innovative photo solution for hospitality",
      contact: "Contact",
      legal: "Legal notice",
      privacy: "Privacy",
      terms: "Terms",
      rights: "All rights reserved"
    },
    // CTA
    cta: {
      title: "Ready to revolutionize your guests' photo experience?",
      subtitle: "Join the hospitality leaders who trust us",
      button: "Request a free demo"
    }
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('fr');

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
